import { type CSSProperties, Fragment, type HTMLAttributes } from 'react';
import { kaiFontFamily } from '../../../../core/tokens';
import { kaiTypographyWeb } from '../../../typography';
import { ReviewFlowScrubber } from '../patterns/ReviewFlowScrubber';
import {
  formatReviewFlowPercent,
  getReviewFlowScrubberMarks,
  resolveReviewFlowScrubberActiveIndex,
  type ReviewFlowScrubberMark,
} from '../reviewFlowScrubberLayout';
import { REVIEW_COLORS, REVIEW_RADII, REVIEW_SPACING } from '../reviewFlowTokens';
import type { ReviewFlowTimelineShuttleDensity, ReviewFlowTimelineShuttleModel, ReviewFlowTimelineShuttleSegmentModel } from '../reviewFlowTypes';
import {
  resolveStyleOverride,
  type StyleOverride,
} from '../styleOverrides';

export interface ReviewFlowTimelineShuttleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  model: ReviewFlowTimelineShuttleModel;
  onSelectItem?: (id: string) => void;
  onSelectSegment?: (id: string) => void;
  styleOverride?: StyleOverride;
}

type SegmentWithMark = {
  segment: ReviewFlowTimelineShuttleSegmentModel;
  mark: ReviewFlowScrubberMark;
};

type CompactSegmentWithMark = SegmentWithMark & {
  endPercent: number;
};

const ALIGNED_SEGMENT_SELECTED_SCALE = 1.08;
const ALIGNED_SEGMENT_TRANSITION = [
  'left 280ms cubic-bezier(0.22, 1, 0.36, 1)',
  'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)',
  'opacity 240ms ease',
  'width 220ms ease',
  'box-shadow 220ms ease',
  'background-color 220ms ease',
  'border-color 220ms ease',
].join(', ');

const ALIGNED_SEGMENT_ENTER_ANIMATION = 'review-flow-shuttle-panel-enter 280ms cubic-bezier(0.22, 1, 0.36, 1) both';
const ALIGNED_SEGMENT_KEYFRAMES = `
@keyframes review-flow-shuttle-panel-enter {
  from {
    opacity: 0;
    transform: translateX(calc(-50% + var(--review-flow-shuttle-enter-offset, 0px))) scale(var(--review-flow-shuttle-scale, 1));
  }
  to {
    opacity: var(--review-flow-shuttle-opacity, 1);
    transform: translateX(-50%) scale(var(--review-flow-shuttle-scale, 1));
  }
}
`;

const FOCUSED_SHUTTLE_SEGMENT_DISTANCE = 1;

const getAlignedSegmentOpacity = (distance: number) => Math.max(0.52, 1 - Math.abs(distance) * 0.34);

const getBaseSegmentButtonStyle = (
  segment: ReviewFlowTimelineShuttleSegmentModel,
  onSelectSegment?: (id: string) => void,
  density: ReviewFlowTimelineShuttleDensity = 'default',
): CSSProperties => ({
  ...(density === 'compact' ? kaiTypographyWeb.xxSmall : {}),
  alignItems: 'flex-start',
  background: segment.selected ? REVIEW_COLORS.surface : 'transparent',
  border: 0,
  borderTop: `2px solid ${segment.accentColor ?? (segment.selected ? REVIEW_COLORS.borderHeavy : REVIEW_COLORS.border)}`,
  borderRadius: segment.selected ? REVIEW_RADII.small : 0,
  color: segment.selected ? REVIEW_COLORS.text : REVIEW_COLORS.textMuted,
  cursor: onSelectSegment ? 'pointer' : 'default',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'inherit',
  gap: density === 'compact' ? 2 : REVIEW_SPACING.xSmall,
  minWidth: 0,
  padding: density === 'compact' ? '4px 8px 0' : `${REVIEW_SPACING.xSmall}px ${REVIEW_SPACING.xSmall}px 0`,
  textAlign: 'left',
});

const getAlignedSegmentButtonStyle = (
  segmentWithMark: SegmentWithMark,
  onSelectSegment?: (id: string) => void,
  density: ReviewFlowTimelineShuttleDensity = 'default',
): CSSProperties => {
  const { segment, mark } = segmentWithMark;
  const compact = density === 'compact';
  const width = compact ? (segment.selected ? 196 : 120) : (segment.selected ? 244 : 148);
  const opacity = segment.selected ? 1 : getAlignedSegmentOpacity(mark.distance);
  const scale = segment.selected ? ALIGNED_SEGMENT_SELECTED_SCALE : 1;

  return {
    ...getBaseSegmentButtonStyle(segment, onSelectSegment, density),
    '--review-flow-shuttle-enter-offset': mark.distance < 0 ? '-36px' : mark.distance > 0 ? '36px' : '0px',
    '--review-flow-shuttle-opacity': String(opacity),
    '--review-flow-shuttle-scale': String(scale),
    animation: ALIGNED_SEGMENT_ENTER_ANIMATION,
    background: segment.selected ? REVIEW_COLORS.surface : 'rgba(255, 255, 255, 0.78)',
    borderRadius: REVIEW_RADII.small,
    boxShadow: compact
      ? '0 3px 10px rgba(29, 29, 29, 0.06)'
      : segment.selected
      ? '0 8px 20px rgba(29, 29, 29, 0.12)'
      : '0 3px 10px rgba(29, 29, 29, 0.06)',
    maxWidth: width,
    minHeight: compact ? 32 : 42,
    overflow: 'hidden',
    padding: compact ? '4px 8px' : `${REVIEW_SPACING.xSmall}px ${REVIEW_SPACING.small}px ${REVIEW_SPACING.xSmall}px`,
    position: 'absolute',
    top: 0,
    left: formatReviewFlowPercent(mark.percent),
    width,
    transform: `translateX(-50%) scale(${scale})`,
    transformOrigin: 'center top',
    transition: ALIGNED_SEGMENT_TRANSITION,
    opacity,
    willChange: 'left, transform, opacity',
    zIndex: segment.selected ? 3 : 1,
  } as CSSProperties;
};

const SegmentButton = ({
  segment,
  style,
  onSelectSegment,
  density = 'default',
  compactLabelLeft,
}: {
  segment: ReviewFlowTimelineShuttleSegmentModel;
  style: CSSProperties;
  onSelectSegment?: (id: string) => void;
  density?: ReviewFlowTimelineShuttleDensity;
  compactLabelLeft?: string;
}) => (
  <button
    key={segment.id}
    type='button'
    aria-label={`Select ${segment.label}`}
    data-review-flow-timeline-shuttle-segment='true'
    data-review-flow-selected={segment.selected ? 'true' : 'false'}
    data-review-flow-segment-id={segment.id}
    onClick={() => onSelectSegment?.(segment.id)}
    style={style}
  >
    {density === 'compact'
      ? (
        <span
          data-review-flow-compact-segment-label='true'
          style={{
            ...(segment.selected ? kaiTypographyWeb.xxxSmallBold : kaiTypographyWeb.xxxSmall),
            display: 'block',
            position: 'absolute',
            left: compactLabelLeft ?? '50%',
            maxWidth: 'calc(100% - 8px)',
            overflow: 'hidden',
            pointerEvents: 'none',
            textOverflow: 'ellipsis',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {segment.label}
          {segment.meta ? ` (${segment.meta})` : ''}
        </span>
      )
      : (
        <>
          <span
            style={{
              ...(segment.selected ? kaiTypographyWeb.xxSmallBold : kaiTypographyWeb.xxSmallMed),
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {segment.label}
          </span>
          {segment.meta
            ? (
              <span style={{ color: REVIEW_COLORS.textSoft, ...kaiTypographyWeb.xxSmall, whiteSpace: 'nowrap' }}>
                {segment.meta}
              </span>
            )
            : null}
        </>
      )}
  </button>
);

const getAlignedSegments = (model: ReviewFlowTimelineShuttleModel): SegmentWithMark[] => {
  const marks = getReviewFlowScrubberMarks(model.scrubber, resolveReviewFlowScrubberActiveIndex(model.scrubber));
  const marksByItemId = new Map(
    marks.flatMap(mark => mark.item ? [[mark.item.id, mark] as const] : []),
  );

  return (model.segments ?? []).flatMap(segment => {
    const mark = marksByItemId.get(segment.scrubberItemId ?? segment.id);
    return mark && Math.abs(mark.distance) <= FOCUSED_SHUTTLE_SEGMENT_DISTANCE ? [{ segment, mark }] : [];
  });
};

const getCompactSegments = (model: ReviewFlowTimelineShuttleModel): CompactSegmentWithMark[] => {
  const marks = getReviewFlowScrubberMarks(model.scrubber, resolveReviewFlowScrubberActiveIndex(model.scrubber));
  const marksByItemId = new Map(marks.flatMap(mark => mark.item ? [[mark.item.id, mark] as const] : []));
  const segments = (model.segments ?? [])
    .flatMap(segment => {
      const mark = marksByItemId.get(segment.scrubberItemId ?? segment.id);
      return mark ? [{ segment, mark }] : [];
    })
    .sort((left, right) => left.mark.percent - right.mark.percent);

  return segments.map((segmentWithMark, index) => ({
    ...segmentWithMark,
    endPercent: segments[index + 1]?.mark.percent ?? 100,
  }));
};

const getCompactSegmentButtonStyle = (
  { segment, mark, endPercent }: CompactSegmentWithMark,
  onSelectSegment?: (id: string) => void,
): CSSProperties => ({
  ...getBaseSegmentButtonStyle(segment, onSelectSegment, 'compact'),
  alignItems: 'center',
  background: segment.selected ? REVIEW_COLORS.surfaceMuted : 'transparent',
  border: 0,
  borderLeft: 0,
  borderTop: 0,
  borderRadius: 0,
  boxShadow: 'none',
  flexDirection: 'row',
  gap: 4,
  justifyContent: 'center',
  left: formatReviewFlowPercent(mark.percent),
  minHeight: 24,
  opacity: 1,
  overflow: segment.selected ? 'visible' : 'hidden',
  padding: '4px',
  position: 'absolute',
  textAlign: 'center',
  top: 0,
  transform: 'none',
  transition: 'background-color 160ms ease, color 160ms ease',
  whiteSpace: 'nowrap',
  width: formatReviewFlowPercent(Math.max(0, endPercent - mark.percent)),
});

const getCompactSegmentLabelLeft = (
  { segment, mark, endPercent }: CompactSegmentWithMark,
  activePercent: number,
) => {
  if (!segment.selected) return '50%';
  const segmentWidth = endPercent - mark.percent;
  if (segmentWidth <= 0) return '50%';
  const relativePercent = ((activePercent - mark.percent) / segmentWidth) * 100;
  return formatReviewFlowPercent(Math.min(Math.max(relativePercent, 0), 100));
};

const TimelineSegments = ({
  model,
  onSelectSegment,
  density,
}: {
  model: ReviewFlowTimelineShuttleModel;
  onSelectSegment?: (id: string) => void;
  density: ReviewFlowTimelineShuttleDensity;
}) => {
  if (!model.segments?.length) return null;

  if (model.segmentLayout === 'scrubber') {
    const alignedSegments = density === 'compact' ? getCompactSegments(model) : getAlignedSegments(model);
    const activePercent = getReviewFlowScrubberMarks(
      model.scrubber,
      resolveReviewFlowScrubberActiveIndex(model.scrubber),
    ).find(mark => mark.distance === 0)?.percent ?? 50;
    if (!alignedSegments.length) return null;

    return (
      <div
        data-review-flow-timeline-segments-layout='scrubber'
        style={{
          position: 'relative',
          minHeight: density === 'compact' ? 24 : 50,
          overflow: 'visible',
        }}
      >
        {density === 'default' ? <style>{ALIGNED_SEGMENT_KEYFRAMES}</style> : null}
        {alignedSegments.map(segmentWithMark => (
          <Fragment key={segmentWithMark.segment.id}>
            {density === 'compact'
              ? (
                <span
                  aria-hidden='true'
                  data-review-flow-compact-segment-boundary='true'
                  style={{
                    position: 'absolute',
                    top: -10,
                    left: formatReviewFlowPercent(segmentWithMark.mark.percent),
                    width: 1,
                    height: 14,
                    background: REVIEW_COLORS.borderStrong,
                    opacity: 0.72,
                  }}
                />
              )
              : null}
            <SegmentButton
              segment={segmentWithMark.segment}
              density={density}
              compactLabelLeft={density === 'compact'
                ? getCompactSegmentLabelLeft(segmentWithMark as CompactSegmentWithMark, activePercent)
                : undefined}
              onSelectSegment={onSelectSegment}
              style={density === 'compact'
                ? getCompactSegmentButtonStyle(segmentWithMark as CompactSegmentWithMark, onSelectSegment)
                : getAlignedSegmentButtonStyle(segmentWithMark, onSelectSegment)}
            />
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      data-review-flow-timeline-segments-layout='grid'
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${model.segments.length}, minmax(0, 1fr))`,
        gap: REVIEW_SPACING.small,
      }}
    >
      {model.segments.map(segment => (
        <SegmentButton
          key={segment.id}
          segment={segment}
          density={density}
          onSelectSegment={onSelectSegment}
          style={getBaseSegmentButtonStyle(segment, onSelectSegment, density)}
        />
      ))}
    </div>
  );
};

export const ReviewFlowTimelineShuttle = ({
  model,
  onSelectItem,
  onSelectSegment,
  styleOverride: shuttleCss,
  style,
  ...props
}: ReviewFlowTimelineShuttleProps) => {
  const density = model.density ?? 'default';
  const compact = density === 'compact';

  return (
    <div
      data-review-flow-timeline-shuttle='true'
      data-review-flow-density={density}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 2 : REVIEW_SPACING.small,
        minHeight: compact ? 78 : 113,
        padding: compact ? '4px 20px 6px' : `${REVIEW_SPACING.small}px ${REVIEW_SPACING.medium}px 14px`,
        borderTop: `1px solid ${REVIEW_COLORS.border}`,
        background: REVIEW_COLORS.canvas,
        color: REVIEW_COLORS.text,
        fontFamily: kaiFontFamily,
        ...style,
        ...resolveStyleOverride(shuttleCss),
      }}
      {...props}
    >
      <div
        style={{
          alignItems: 'center',
          display: 'grid',
          gap: compact ? REVIEW_SPACING.small : REVIEW_SPACING.medium,
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          minWidth: 0,
        }}
      >
        {model.title
          ? (
            <div
              style={{
                color: REVIEW_COLORS.textMuted,
                ...(compact ? kaiTypographyWeb.xxxSmallMed : kaiTypographyWeb.xxSmallBold),
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {model.title}
            </div>
          )
          : <span />}

        {model.activeLabel || model.countLabel
          ? (
            <div style={{ alignItems: 'center', display: 'flex', gap: REVIEW_SPACING.small, minWidth: 0 }}>
              {model.activeLabel
                ? (
                  <span
                    style={{
                      color: REVIEW_COLORS.text,
                      ...(compact ? kaiTypographyWeb.xxxSmallBold : kaiTypographyWeb.xxSmallBold),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {model.activeLabel}
                  </span>
                )
                : null}
              {model.countLabel
                ? (
                  <span
                    style={{ color: REVIEW_COLORS.textSoft, ...(compact ? kaiTypographyWeb.xxxSmall : kaiTypographyWeb.xxSmallBold), whiteSpace: 'nowrap' }}
                  >
                    {model.countLabel}
                  </span>
                )
                : null}
            </div>
          )
          : null}
      </div>

      <div data-testid='review-flow-timeline-shuttle-scrubber'>
        <ReviewFlowScrubber
          model={model.scrubber}
          density={density}
          onSelectItem={onSelectItem}
        />
      </div>

      <TimelineSegments model={model} density={density} onSelectSegment={onSelectSegment} />
    </div>
  );
};
